import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Send httpOnly auth cookies on every axios call by default
const authClient = axios.create({ baseURL: `${API}/api`, withCredentials: true });

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 6 * 60 * 60 * 1000; // 6 hours

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);

  const clearAuth = useCallback(async () => {
    try { await authClient.post('/auth/logout'); } catch { /* ignore */ }
    // last_activity is non-sensitive (just a timestamp); kept in localStorage
    localStorage.removeItem('last_activity');
    // Clean up legacy token keys from pre-cookie migration
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    setUser(null);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    localStorage.setItem('last_activity', Date.now().toString());
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      clearAuth().finally(() => { window.location.href = '/login'; });
    }, INACTIVITY_TIMEOUT);
  }, [clearAuth]);

  // Track user activity
  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [user, resetInactivityTimer]);

  const checkAuth = useCallback(async () => {
    try {
      const lastActivity = localStorage.getItem('last_activity');
      if (lastActivity && (Date.now() - parseInt(lastActivity)) > INACTIVITY_TIMEOUT) {
        await clearAuth();
        setLoading(false);
        return;
      }
      // Cookie-based session check. If cookie expired, try one refresh.
      try {
        const response = await authClient.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          try {
            await authClient.post('/auth/refresh'); // sets new access_token cookie
            const response = await authClient.get('/auth/me');
            setUser(response.data);
          } catch {
            setUser(null);
          }
        } else {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authClient.post('/auth/login', { email, password });
    localStorage.setItem('last_activity', Date.now().toString());
    // Purge any legacy tokens from the old localStorage-based auth
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await clearAuth();
  };

  const forgotPassword = async (email) => {
    const response = await authClient.post('/auth/forgot-password', { email });
    return response.data;
  };

  const verifyOTP = async (email, otp) => {
    const response = await authClient.post('/auth/verify-otp', { email, otp });
    return response.data;
  };

  const resetPassword = async (email, otp, newPassword) => {
    const response = await authClient.post('/auth/reset-password', { email, otp, new_password: newPassword });
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      forgotPassword,
      verifyOTP,
      resetPassword,
      checkAuth,
      isAdmin: user?.roles?.includes('admin') || user?.role === 'admin',
      isManager: user?.roles?.includes('admin') || user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
